import { useEffect, useMemo, useRef } from "react";
import {
  useInfiniteQuery, useMutation, useQuery, useQueryClient,
  type QueryClient, type QueryKey,
} from "@tanstack/react-query";
import { JmapApi, MailScheduleApi, http } from "@/api";
import type { ScheduledEmail } from "@/api";
import type { SchedulePayload } from "@/api/mailSchedule";
import { qk } from "./queryKeys";
import type { AccountId, Email, Mailbox } from "@/types";

// ── Optimistic-mutation plumbing ─────────────────────────────────────────────
// Triage actions (star / read / archive / delete) must feel instant. Each of
// the mutations below patches the React Query cache in onMutate, keeps a
// snapshot of the whole `mail` namespace to roll back on error, and revalidates
// on settle. The list is an infinite query, so we walk every cached page.

interface ThreadsPage { emails: Email[]; total: number; nextPosition: number }
interface ThreadsInfinite { pages: ThreadsPage[]; pageParams: unknown[] }

// Prefixes of the hierarchical keys in queryKeys.ts. Matching by prefix lets a
// single patch hit every cached mailbox/filter/label variant at once.
const THREADS_KEY = [...qk.mail, "threads"] as const;
const EMAIL_KEY = [...qk.mail, "email"] as const;
const CONVO_KEY = [...qk.mail, "thread"] as const;

const MAILBOX_KEY = [...qk.mail, "mailbox"] as const;

// Invalidate only what a mail mutation can actually change.
//
// These call sites used to invalidate all of qk.mail, which marks every active
// query under it stale and refetches immediately: the JMAP session, sending
// identities, the undo-send window, scheduled mail, labels, the mailbox list,
// the open email, the conversation, AND every loaded page of the infinite
// thread list. Ten pages deep, one star or archive cost ten sequential JMAP
// round trips plus a handful of unrelated ones.
function invalidateMailData(qc: QueryClient): void {
  void qc.invalidateQueries({ queryKey: THREADS_KEY });
  void qc.invalidateQueries({ queryKey: EMAIL_KEY });
  void qc.invalidateQueries({ queryKey: CONVO_KEY });
  void qc.invalidateQueries({ queryKey: MAILBOX_KEY });
}

// Apply a keyword delta to a set of email ids across the list, single-email,
// and conversation caches. Used by star / mark-read (and their bulk variants).
function patchThreadKeywords(
  qc: QueryClient,
  ids: string[],
  kw: Partial<Email["keywords"]>,
): void {
  const idset = new Set(ids);
  const merge = (e: Email): Email =>
    idset.has(e.id) ? { ...e, keywords: { ...e.keywords, ...kw } } : e;

  for (const [key, data] of qc.getQueriesData<ThreadsInfinite>({ queryKey: THREADS_KEY })) {
    if (!data) continue;
    qc.setQueryData<ThreadsInfinite>(key, {
      ...data,
      pages: data.pages.map((p) => ({ ...p, emails: p.emails.map(merge) })),
    });
  }
  for (const [key, data] of qc.getQueriesData<Email>({ queryKey: EMAIL_KEY })) {
    if (data && idset.has(data.id)) qc.setQueryData<Email>(key, merge(data));
  }
  for (const [key, data] of qc.getQueriesData<Email[]>({ queryKey: CONVO_KEY })) {
    if (!data) continue;
    if (data.some((e) => idset.has(e.id))) qc.setQueryData<Email[]>(key, data.map(merge));
  }
}

// Drop email ids out of every cached list page (leaving the current folder) and
// decrement the honest per-folder total so the "N messages" count stays right.
function removeFromThreadCaches(qc: QueryClient, ids: string[]): void {
  const idset = new Set(ids);
  for (const [key, data] of qc.getQueriesData<ThreadsInfinite>({ queryKey: THREADS_KEY })) {
    if (!data) continue;
    let removed = 0;
    const pages = data.pages.map((p) => {
      const emails = p.emails.filter((e) => {
        if (idset.has(e.id)) { removed++; return false; }
        return true;
      });
      return { ...p, emails };
    });
    if (removed === 0) continue;
    qc.setQueryData<ThreadsInfinite>(key, {
      ...data,
      pages: pages.map((p) => ({ ...p, total: Math.max(0, p.total - removed) })),
    });
  }
}

// Snapshot every `mail` query so onError can restore the pre-mutation view.
function snapshotMail(qc: QueryClient): [QueryKey, unknown][] {
  return qc.getQueriesData({ queryKey: qk.mail });
}
function restoreMail(qc: QueryClient, snap?: [QueryKey, unknown][]): void {
  if (!snap) return;
  for (const [key, data] of snap) qc.setQueryData(key, data);
}

// One-shot Email/set batch through the JMAP proxy. Lets the bulk actions push a
// single request that updates/destroys many ids at once (a true JMAP batch),
// mirroring jmap.ts's private jmapBatch without reaching into it.
async function jmapEmailSet(
  accountId: AccountId,
  ops: { update?: Record<string, Record<string, unknown>>; destroy?: string[] },
): Promise<void> {
  const res = await fetch("/api/jmap", {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json", "Origin": window.location.origin },
    body: JSON.stringify({
      using: ["urn:ietf:params:jmap:core", "urn:ietf:params:jmap:mail"],
      methodCalls: [["Email/set", { accountId, ...ops }, "0"]],
    }),
  });
  if (!res.ok) throw new Error(`JMAP Email/set batch failed: ${res.status}`);
  const body = (await res.json()) as { methodResponses?: Array<[string, unknown, string]> };
  const r = body.methodResponses?.[0];
  if (!r) throw new Error("JMAP response missing methodResponses");
  if (r[0] === "error") {
    const e = r[1] as { type?: string; description?: string };
    throw new Error(`JMAP Email/set error: ${e.type ?? "unknown"}${e.description ? ` — ${e.description}` : ""}`);
  }
}

export interface SendingIdentity {
  email: string;
  label: string;
  is_primary: boolean;
}

export function useSendingIdentities() {
  return useQuery<SendingIdentity[]>({
    queryKey: ["mail", "sending-identities"],
    queryFn: () => http<SendingIdentity[]>("/api/me/sending-identities"),
    staleTime: 60_000 * 30,
  });
}

export function useSendAs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Parameters<typeof JmapApi.sendAs>[0]) => JmapApi.sendAs(body),
    onSuccess: () => invalidateMailData(qc),
  });
}

// Resolves the JMAP session (capabilities + accountId) once per app load.
// The Worker handles the actual session.fetch + per-user secret lookup.
export function useMailSession() {
  return useQuery({
    queryKey: ["mail", "session"],
    queryFn: () => JmapApi.session(),
    staleTime: 60_000 * 30,           // session rarely changes during a tab lifetime
    retry: 1,
  });
}

export function useMailboxes(account_id: AccountId | undefined) {
  return useQuery<Mailbox[]>({
    queryKey: account_id ? qk.mailbox(account_id) : ["mail", "mailbox", "_pending"],
    enabled: !!account_id,
    queryFn: async () => (await JmapApi.listMailboxes(account_id!)).list,
  });
}

export type ThreadFilter = "all" | "unread" | "starred" | "has_attachment";

export function useThreads(
  account_id: AccountId | undefined,
  mailbox_id: string | null,
  opts: { filter?: ThreadFilter; sortAsc?: boolean; label?: string } = {},
) {
  const filter = opts.filter ?? "all";
  const sortAsc = opts.sortAsc ?? false;
  // A label view is cross-folder: mailbox_id is null and we query by hasKeyword
  // across all mail. The query stays enabled when EITHER a mailbox or a label
  // is selected.
  const label = opts.label ?? undefined;
  return useInfiniteQuery({
    queryKey: qk.mailThreads(
      account_id ?? "_",
      mailbox_id ?? (label ? `label:${label}` : "_"),
      { filter, sortAsc, label: label ?? null },
    ),
    enabled: !!account_id && (!!mailbox_id || !!label),
    initialPageParam: 0 as number,
    // 2-minute staleTime — the inbox list doesn't need to re-poll on every focus
    // switch, because useMailPush invalidates these queries when Stalwart
    // reports a change (and the service worker does the same on Web Push when
    // the tab is backgrounded). So a longer staleTime just means tab switches
    // and mailbox revisits render instantly from cache instead of refetching.
    // Note this only holds while that subscription exists: without it, this
    // staleTime is how long new mail stays invisible.
    staleTime: 120_000,
    queryFn: async ({ pageParam }) => {
      // Translate Layer7 filter → JMAP Email/query filter object. Each active
      // condition is its own JMAP FilterCondition; two or more are combined
      // under an explicit AND operator (a single top-level object can't carry
      // e.g. hasKeyword=$flagged AND hasKeyword=$label_work at once).
      const conds: Record<string, unknown>[] = [];
      if (filter === "unread") conds.push({ notKeyword: "$seen" });
      if (filter === "starred") conds.push({ hasKeyword: "$flagged" });
      if (filter === "has_attachment") conds.push({ hasAttachment: true });
      if (label) conds.push({ hasKeyword: label });
      let jmapFilter: Record<string, unknown> | undefined;
      if (conds.length === 1) jmapFilter = conds[0];
      else if (conds.length > 1) jmapFilter = { operator: "AND", conditions: conds };
      const res = await JmapApi.queryAndGet(account_id!, {
        mailboxId: mailbox_id ?? undefined,
        filter: jmapFilter,
        sort: [{ property: "receivedAt", isAscending: sortAsc }],
        position: pageParam,
        limit: 40,
        collapseThreads: true,
      });
      return res;
    },
    getNextPageParam: (last) => (last.nextPosition < last.total ? last.nextPosition : undefined),
  });
}

export function useEmail(account_id: AccountId | undefined, id: string | null) {
  return useQuery<Email>({
    queryKey: qk.mailEmail(account_id ?? "_", id ?? "_"),
    enabled: !!account_id && !!id,
    queryFn: async () => (await JmapApi.getEmails(account_id!, [id!])).list[0],
  });
}

// Full conversation: every Email in a JMAP Thread, ordered oldest → newest.
// Used by the Reader to render Outlook/iCloud-style stacked message bubbles.
// Single round-trip via JMAP back-references (Thread/get → Email/get inside
// one batch). Cuts thread-open latency roughly in half vs two sequential
// network round trips.
export function useThread(account_id: AccountId | undefined, thread_id: string | null) {
  return useQuery<Email[]>({
    queryKey: qk.mailThread(account_id ?? "_", thread_id ?? "_"),
    enabled: !!account_id && !!thread_id,
    queryFn: async () => {
      const list = await JmapApi.getThreadWithEmails(account_id!, thread_id!);
      // Sort oldest → newest so the conversation reads top-to-bottom like
      // Outlook/iCloud. JMAP doesn't promise a specific order from Email/get.
      return list.sort((a, b) => {
        const ta = new Date(a.sent_at ?? a.received_at).getTime();
        const tb = new Date(b.sent_at ?? b.received_at).getTime();
        return ta - tb;
      });
    },
    staleTime: 10_000,
  });
}

// Mutation wrapper around the AI cleanup endpoint. Returns the cleaned HTML
// without persisting to the server — the UI swaps it in only for this render.
export function useAiCleanupEmail() {
  return useMutation({
    mutationFn: async (html: string): Promise<string> => {
      const res = await http<{ html: string }>("/api/mail/ai-cleanup", {
        method: "POST",
        body: JSON.stringify({ html }),
      });
      return res.html;
    },
  });
}

export function useMarkRead(account_id: AccountId | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, seen }: { id: string; seen: boolean }) => {
      if (!account_id) throw new Error("No account_id");
      return JmapApi.setKeywords(account_id, id, { $seen: seen });
    },
    onMutate: async ({ id, seen }) => {
      await qc.cancelQueries({ queryKey: qk.mail });
      const snap = snapshotMail(qc);
      patchThreadKeywords(qc, [id], { $seen: seen });
      return { snap };
    },
    onError: (_e, _v, ctx) => restoreMail(qc, ctx?.snap),
    onSettled: () => invalidateMailData(qc),
  });
}

// Mark every unread message in a mailbox as read. Returns the count marked.
export function useMarkAllRead(account_id: AccountId | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ mailboxId }: { mailboxId: string }) => {
      if (!account_id) throw new Error("No account_id");
      return JmapApi.markMailboxRead(account_id, mailboxId);
    },
    onSuccess: () => invalidateMailData(qc),
  });
}

export function useStar(account_id: AccountId | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, on }: { id: string; on: boolean }) => {
      if (!account_id) throw new Error("No account_id");
      return JmapApi.setKeywords(account_id, id, { $flagged: on });
    },
    onMutate: async ({ id, on }) => {
      await qc.cancelQueries({ queryKey: qk.mail });
      const snap = snapshotMail(qc);
      patchThreadKeywords(qc, [id], { $flagged: on });
      return { snap };
    },
    onError: (_e, _v, ctx) => restoreMail(qc, ctx?.snap),
    onSettled: () => invalidateMailData(qc),
  });
}

export function useSend(account_id: AccountId | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Parameters<typeof JmapApi.send>[1]) => {
      if (!account_id) throw new Error("No account_id");
      return JmapApi.send(account_id, body);
    },
    onSuccess: () => invalidateMailData(qc),
  });
}

// Persist a composer draft (create-new + destroy-old, since JMAP Emails are
// immutable). The Drafts mailbox id is resolved once and cached in a
// module-level ref so we don't re-query it on every keystroke-triggered save.
let draftsMailboxIdCache: { account: AccountId; id: string } | null = null;

export function useSaveDraft(account_id: AccountId | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      d: Omit<Parameters<typeof JmapApi.saveDraft>[1], "draftsMailboxId">,
    ): Promise<{ id: string; threadId: string }> => {
      if (!account_id) throw new Error("No account_id");
      if (draftsMailboxIdCache?.account !== account_id) {
        const id = await JmapApi.ensureDraftsMailbox(account_id);
        draftsMailboxIdCache = { account: account_id, id };
      }
      return JmapApi.saveDraft(account_id, { ...d, draftsMailboxId: draftsMailboxIdCache.id });
    },
    onSuccess: () => invalidateMailData(qc),
  });
}

export function useDeleteDraft(account_id: AccountId | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => {
      if (!account_id) throw new Error("No account_id");
      return JmapApi.deleteDraft(account_id, id);
    },
    onSuccess: () => invalidateMailData(qc),
  });
}

export function useMoveEmail(account_id: AccountId | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, mailboxIds }: { id: string; mailboxIds: string[] }) => {
      if (!account_id) throw new Error("No account_id");
      return JmapApi.move(account_id, id, mailboxIds);
    },
    onMutate: async ({ id }) => {
      await qc.cancelQueries({ queryKey: qk.mail });
      const snap = snapshotMail(qc);
      // Leaving the current folder — drop it from every cached list so the row
      // disappears instantly (archive / move feel immediate).
      removeFromThreadCaches(qc, [id]);
      return { snap };
    },
    onError: (_e, _v, ctx) => restoreMail(qc, ctx?.snap),
    onSettled: () => invalidateMailData(qc),
  });
}

export function useDeleteEmail(account_id: AccountId | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => {
      if (!account_id) throw new Error("No account_id");
      return JmapApi.delete(account_id, ids);
    },
    onMutate: async (ids) => {
      await qc.cancelQueries({ queryKey: qk.mail });
      const snap = snapshotMail(qc);
      removeFromThreadCaches(qc, ids);
      return { snap };
    },
    onError: (_e, _v, ctx) => restoreMail(qc, ctx?.snap),
    onSettled: () => invalidateMailData(qc),
  });
}

// ── Bulk triage (multi-select) ───────────────────────────────────────────────
// Each pushes ONE JMAP Email/set covering every selected id, with the same
// optimistic-patch + rollback + revalidate contract as the single-item hooks.

export function useBulkMarkRead(account_id: AccountId | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, seen }: { ids: string[]; seen: boolean }) => {
      if (!account_id) throw new Error("No account_id");
      if (ids.length === 0) return Promise.resolve();
      const update: Record<string, Record<string, unknown>> = {};
      for (const id of ids) update[id] = { "keywords/$seen": seen };
      return jmapEmailSet(account_id, { update });
    },
    onMutate: async ({ ids, seen }) => {
      await qc.cancelQueries({ queryKey: qk.mail });
      const snap = snapshotMail(qc);
      patchThreadKeywords(qc, ids, { $seen: seen });
      return { snap };
    },
    onError: (_e, _v, ctx) => restoreMail(qc, ctx?.snap),
    onSettled: () => invalidateMailData(qc),
  });
}

export function useBulkMove(account_id: AccountId | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, mailboxId }: { ids: string[]; mailboxId: string }) => {
      if (!account_id) throw new Error("No account_id");
      if (ids.length === 0) return Promise.resolve();
      const update: Record<string, Record<string, unknown>> = {};
      for (const id of ids) update[id] = { mailboxIds: { [mailboxId]: true } };
      return jmapEmailSet(account_id, { update });
    },
    onMutate: async ({ ids }) => {
      await qc.cancelQueries({ queryKey: qk.mail });
      const snap = snapshotMail(qc);
      removeFromThreadCaches(qc, ids);
      return { snap };
    },
    onError: (_e, _v, ctx) => restoreMail(qc, ctx?.snap),
    onSettled: () => invalidateMailData(qc),
  });
}

// Bulk delete: when a Trash mailbox is supplied and the messages aren't already
// there, this is a recoverable move-to-Trash. Pass destroy=true (already in
// Trash, or no Trash mailbox) to permanently remove.
export function useBulkDelete(account_id: AccountId | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, destroy, trashMailboxId }: { ids: string[]; destroy: boolean; trashMailboxId?: string }) => {
      if (!account_id) throw new Error("No account_id");
      if (ids.length === 0) return Promise.resolve();
      if (destroy || !trashMailboxId) return jmapEmailSet(account_id, { destroy: ids });
      const update: Record<string, Record<string, unknown>> = {};
      for (const id of ids) update[id] = { mailboxIds: { [trashMailboxId]: true } };
      return jmapEmailSet(account_id, { update });
    },
    onMutate: async ({ ids }) => {
      await qc.cancelQueries({ queryKey: qk.mail });
      const snap = snapshotMail(qc);
      removeFromThreadCaches(qc, ids);
      return { snap };
    },
    onError: (_e, _v, ctx) => restoreMail(qc, ctx?.snap),
    onSettled: () => invalidateMailData(qc),
  });
}

// ── Schedule send ───────────────────────────────────────────────────────────
// Separate from JMAP: these hit the Worker's /api/mail/schedule* endpoints,
// which queue the message and fire it server-side at fire_at (ms epoch).

export function useScheduledEmails() {
  return useQuery<ScheduledEmail[]>({
    queryKey: ["mail", "scheduled"],
    queryFn: () => MailScheduleApi.list(),
    staleTime: 30_000,
  });
}

export function useScheduleEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ payload, files }: { payload: SchedulePayload; files: File[] }) =>
      MailScheduleApi.schedule(payload, files),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["mail", "scheduled"] }),
  });
}

export function useCancelScheduled() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => MailScheduleApi.cancel(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["mail", "scheduled"] }),
  });
}

export function useRescheduleEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, fireAt }: { id: string; fireAt: number }) =>
      MailScheduleApi.reschedule(id, fireAt),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["mail", "scheduled"] }),
  });
}

// ── Undo Send window ──────────────────────────────────────────────────────
// Per-user "hold before send" duration (seconds). A normal send is parked in
// the durable queue for this long so the client can show an Undo action. 0 =
// relay instantly. Backed by /api/mail/prefs/undo-window (get/put in mail.ts).

export interface UndoWindowPref {
  seconds: number;
  choices: number[];
}

export function useUndoWindow() {
  return useQuery<UndoWindowPref>({
    queryKey: ["mail", "undo-window"],
    queryFn: () => http<UndoWindowPref>("/api/mail/prefs/undo-window"),
    staleTime: 60_000 * 10,
  });
}

export function useSetUndoWindow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (seconds: number) =>
      http<UndoWindowPref>("/api/mail/prefs/undo-window", {
        method: "PUT",
        body: JSON.stringify({ seconds }),
      }),
    onSuccess: (data) => {
      qc.setQueryData(["mail", "undo-window"], data);
      qc.invalidateQueries({ queryKey: ["mail", "undo-window"] });
    },
  });
}

// ── Labels (JMAP custom keywords) ────────────────────────────────────────────
// A label is a custom JMAP keyword ($label_<slug>) toggled on messages via
// Email/set and queried via Email/query hasKeyword. The keyword itself is the
// source of truth server-side; the client keeps only the label *registry*
// (display name + colour per keyword) in localStorage, since JMAP has no place
// to store a colour. No backend migration is needed.

export const LABEL_KEYWORD_PREFIX = "$label_";
const LABEL_STORE_KEY = "l7:mail:labels";

export interface MailLabel {
  keyword: string; // full JMAP keyword, e.g. "$label_work" — also the id
  name: string;
  color: string;   // any CSS color; rendered as the chip dot
}

// Slug a human name into a valid, lowercase JMAP keyword. JMAP keywords are
// case-insensitive atoms; we restrict to [a-z0-9_] to stay safely inside that.
export function labelNameToKeyword(name: string): string {
  const slug = name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  return LABEL_KEYWORD_PREFIX + (slug || "label");
}

// Which label keywords are currently set on a message (truthy only).
export function extractLabels(keywords: Email["keywords"]): string[] {
  return Object.keys(keywords).filter((k) => k.startsWith(LABEL_KEYWORD_PREFIX) && keywords[k]);
}

function readLabelStore(): MailLabel[] {
  try {
    const raw = localStorage.getItem(LABEL_STORE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (l): l is MailLabel =>
        !!l && typeof l.keyword === "string" && typeof l.name === "string" && typeof l.color === "string",
    );
  } catch {
    return [];
  }
}

// Registry of labels + create/rename/delete.
//
// Server-backed (user_prefs via /api/me/mail-labels). It used to live only in
// localStorage, so a label created on the desktop rendered as nothing on the
// laptop — LabelChip returns null for a keyword it doesn't know — and clearing
// site data destroyed every label name while the keywords stayed on the
// messages. The keyword remains authoritative on the server; this registry only
// carries the display name and colour, which JMAP has nowhere to put.
export function useLabels() {
  const qc = useQueryClient();
  const migrated = useRef(false);

  const query = useQuery<MailLabel[]>({
    queryKey: ["mail", "labels"],
    queryFn: async () => {
      const res = await http<{ labels: MailLabel[] }>("/api/me/mail-labels");
      return res.labels ?? [];
    },
    staleTime: 5 * 60_000,
  });
  const labels = useMemo(() => query.data ?? [], [query.data]);

  const save = useMutation({
    mutationFn: (next: MailLabel[]) =>
      http<{ labels: MailLabel[] }>("/api/me/mail-labels", {
        method: "PUT",
        body: JSON.stringify({ labels: next }),
      }),
    // Optimistic: sidebar and chips update on the same tick as the click.
    onMutate: async (next) => {
      await qc.cancelQueries({ queryKey: ["mail", "labels"] });
      const prev = qc.getQueryData<MailLabel[]>(["mail", "labels"]);
      qc.setQueryData(["mail", "labels"], next);
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(["mail", "labels"], ctx.prev);
    },
    onSuccess: (res) => qc.setQueryData(["mail", "labels"], res.labels ?? []),
  });

  // One-time lift of whatever is already in localStorage. Without it the switch
  // to server storage looks like every existing label vanishing. Runs only when
  // the server has none, so it cannot resurrect deletions.
  useEffect(() => {
    if (migrated.current || query.isLoading || query.isError) return;
    migrated.current = true;
    if ((query.data ?? []).length > 0) return;
    const local = readLabelStore();
    if (local.length === 0) return;
    save.mutate(local, {
      onSuccess: () => {
        try { localStorage.removeItem(LABEL_STORE_KEY); } catch { /* best effort */ }
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.isLoading, query.isError, query.data]);

  const persist = (next: MailLabel[]) => save.mutate(next);

  const createLabel = (name: string, color: string): string => {
    const keyword = labelNameToKeyword(name);
    const trimmed = name.trim();
    if (!trimmed) return keyword;
    if (labels.some((l) => l.keyword === keyword)) return keyword; // idempotent
    persist([...labels, { keyword, name: trimmed, color }]);
    return keyword;
  };
  const deleteLabel = (keyword: string) => persist(labels.filter((l) => l.keyword !== keyword));
  const renameLabel = (keyword: string, name: string) =>
    persist(labels.map((l) => (l.keyword === keyword ? { ...l, name: name.trim() || l.name } : l)));

  return { labels, createLabel, deleteLabel, renameLabel };
}

// Toggle a label keyword on one or more messages (assign / unassign). Optimistic
// so the chip appears/disappears immediately.
export function useAssignLabel(account_id: AccountId | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, keyword, on }: { ids: string[]; keyword: string; on: boolean }) => {
      if (!account_id) throw new Error("No account_id");
      if (ids.length === 0) return Promise.resolve();
      const update: Record<string, Record<string, unknown>> = {};
      for (const id of ids) update[id] = { [`keywords/${keyword}`]: on ? true : null };
      return jmapEmailSet(account_id, { update });
    },
    onMutate: async ({ ids, keyword, on }) => {
      await qc.cancelQueries({ queryKey: qk.mail });
      const snap = snapshotMail(qc);
      patchThreadKeywords(qc, ids, { [keyword]: on });
      return { snap };
    },
    onError: (_e, _v, ctx) => restoreMail(qc, ctx?.snap),
    onSettled: () => invalidateMailData(qc),
  });
}
